import React, { useContext, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Link } from "@reach/router";
import { Search, ArrowLeft, ArrowRight } from "react-feather";
import moment from "moment";
import BackLink from "../../../components/BackLink";
import ListItem from "../../../components/ListItem";
import Header from "../../../components/Header";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE_SCHOOLS } from "../services/graphql";
import styles from "../Franchises.module.scss";

const FranchiseSchools = ({ franchiseId, portal = false }) => {
  const { currentUser } = useContext(AuthContext);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "all", page: 1 });
  const { data, loading, error } = useQuery(GET_FRANCHISE_SCHOOLS, {
    fetchPolicy: "no-cache",
    variables: { franchiseId, ...filters, perPage: 6 }
  });
  const [searchFilters, setSearchFilters] = useState({ search: "", page: 1 });
  const { data: searchData, loading: searching, error: searchError } = useQuery(GET_FRANCHISE_SCHOOLS, {
    skip: !searchFilters.search,
    fetchPolicy: "no-cache",
    variables: { franchiseId, ...searchFilters, status: "all", perPage: 6 }
  });
  const searchResults = (searchData && searchData.franchiseSchools) || {};
  const directory = (data && data.franchiseSchools) || {};
  const schools = directory.schools || [];
  const pageNumbers = directory.pageNumbers || [];

  const handleSearch = event => {
    event.preventDefault();
    setSearchFilters({ search: search.trim(), page: 1 });
  };

  const changePage = page => {
    setFilters({ ...filters, page });
  };

  const schoolDirectory = (
        <div className={styles.section}>
          <div className={styles.directoryHeading}>
            <h2>School directory</h2>
            <span>{(directory.filteredCount || 0).toLocaleString("en-GB")} schools</span>
          </div>
          {schools.length === 0 ? <p>No schools found.</p> : (
            <div className={styles.schoolsTableWrapper}>
              <table className={`${styles.schoolsTable} ${styles.directoryTable}`}>
                <thead>
                  <tr>
                    <th scope="col">School</th>
                    <th scope="col">Code</th>
                    <th scope="col">Pupils</th>
                    <th scope="col">Admin</th>
                    <th scope="col">Start</th>
                    <th scope="col">Status</th>
                    <th scope="col"><span className={styles.visuallyHidden}>Open school</span></th>
                  </tr>
                </thead>
                <tbody>
                  {schools.map(school => (
                    <tr key={school.id}>
                      <th scope="row"><Link to={portal ? `/franchise-portal/${franchiseId}/schools/${school.id}` : `/franchises/${franchiseId}/schools/${school.id}`}>{school.name}</Link></th>
                      <td>{school.schoolCode || "—"}</td>
                      <td>{(school.pupilsCount || 0).toLocaleString("en-GB")}</td>
                      <td>{school.primaryAdministratorName || "—"}</td>
                      <td>{school.startDate ? moment(school.startDate).format("DD MMM YY") : "—"}</td>
                      <td>
                        <span className={styles.detailStatus} data-status={(school.onboardingStatusLabel || "").toLowerCase()}>
                          {school.onboardingStatusLabel}
                        </span>
                      </td>
                      <td>
                        <Link to={portal ? `/franchise-portal/${franchiseId}/schools/${school.id}` : `/franchises/${franchiseId}/schools/${school.id}`} aria-label={`Open ${school.name}`}>
                          <ArrowRight size={16} aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.directoryFooter}>
            <p>{directory.showingLabel || "Showing 0 schools"}</p>
            {directory.totalPages > 1 && (
              <nav className={styles.pagination} aria-label="School pages">
                <button type="button" aria-label="Previous page" disabled={!directory.hasPreviousPage} onClick={() => changePage(filters.page - 1)}>
                  <ArrowLeft size={16} aria-hidden="true" />
                </button>
                {pageNumbers.map(page => (
                  <button type="button" key={page} aria-label={`Page ${page}`} aria-current={page === directory.page ? "page" : undefined} onClick={() => changePage(page)}>
                    {page}
                  </button>
                ))}
                <button type="button" aria-label="Next page" disabled={!directory.hasNextPage} onClick={() => changePage(filters.page + 1)}>
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </nav>
            )}
          </div>
        </div>
  );

  return (
    <div className={portal ? `${styles.franchises} ${styles.portal}` : styles.franchises}>
      {portal ? (
        <Header user={currentUser} portalFranchise={directory.franchise} portalFranchiseId={franchiseId} portalPage="schools" />
      ) : (
        <BackLink text="Franchise" to={`/franchises/${franchiseId}`} />
      )}
      <div className={`${styles.header} ${styles.detailHeader}`}>
        <div>
          <p className={styles.territoryLabel}>{directory.territoryLabel}</p>
          <h1 className="title">Schools</h1>
          <p>Search, open and manage every school in your franchise.</p>
        </div>
        <Link to={portal ? `/franchise-portal/${franchiseId}/schools/new` : `/franchises/${franchiseId}/schools/new`} className={styles.primaryButton}>Add school</Link>
      </div>

      <form className={styles.schoolSearch} onSubmit={handleSearch}>
        <div className={styles.searchInput}>
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            aria-label="Search by school name, code or administrator name"
            placeholder="Search by school name, code or administrator name"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        <select aria-label="School status" value={filters.status} onChange={event => setFilters({ ...filters, status: event.target.value, page: 1 })}>
          <option value="all">Status: All</option>
          <option value="active">Status: Active</option>
          <option value="in_setup">Status: In setup</option>
          <option value="needs_admin">Status: Needs admin</option>
          <option value="no_admin">Status: No admin</option>
          <option value="setup">Status: Setup</option>
          <option value="deactivated">Status: Deactivated</option>
        </select>
        <button type="submit" className={styles.primaryButton} disabled={searching}>Search</button>
      </form>

      {searchFilters.search && <section className="u-m-base-3" aria-label="School search results">
        <div className={styles.directoryHeading}>
          <h2>Search results</h2>
          {!searching && !searchError && <span>{(searchResults.filteredCount || 0).toLocaleString("en-GB")} schools</span>}
        </div>
        {searching && <p role="status">Searching schools...</p>}
        {searchError && <p role="alert">Unable to search schools. Please try again.</p>}
        {!searching && !searchError && <>
          {(searchResults.schools || []).map(school => (
            <ListItem
              key={school.id}
              linkPath={portal ? `/franchise-portal/${franchiseId}/schools/${school.id}` : `/franchises/${franchiseId}/schools/${school.id}`}
              mainText={<>{school.name} <span className="light body-text">{school.schoolCode}</span></>}
            />
          ))}
          {!(searchResults.schools || []).length && <p>No schools found.</p>}
          <div className={styles.directoryFooter}>
            {searchResults.totalPages > 1 && (
              <nav className={styles.pagination} aria-label="Search result pages">
                <button type="button" aria-label="Previous search results" disabled={!searchResults.hasPreviousPage}
                  onClick={() => setSearchFilters({ ...searchFilters, page: searchFilters.page - 1 })}>
                  <ArrowLeft size={16} aria-hidden="true" />
                </button>
                {(searchResults.pageNumbers || []).map(page => (
                  <button type="button" key={page} aria-label={`Search results page ${page}`} aria-current={page === searchResults.page ? "page" : undefined}
                    onClick={() => setSearchFilters({ ...searchFilters, page })}>{page}</button>
                ))}
                <button type="button" aria-label="Next search results" disabled={!searchResults.hasNextPage}
                  onClick={() => setSearchFilters({ ...searchFilters, page: searchFilters.page + 1 })}>
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </nav>
            )}
          </div>
        </>}
      </section>}

      {loading && <p role="status">Loading schools...</p>}
      {error && <p role="alert">Unable to load schools. Please try again later.</p>}
      {!loading && !error && <>
        <div className={styles.overview}>
          <div className={styles.overviewCard}>
            <h3>All schools</h3>
            <strong>{(directory.totalCount || 0).toLocaleString("en-GB")}</strong>
          </div>
          <div className={styles.overviewCard}>
            <h3>Active</h3>
            <strong>{(directory.activeCount || 0).toLocaleString("en-GB")}</strong>
          </div>
          <div className={styles.overviewCard}>
            <h3>In setup</h3>
            <strong>{(directory.inSetupCount || 0).toLocaleString("en-GB")}</strong>
          </div>
          <div className={styles.overviewCard}>
            <h3>No admin</h3>
            <strong className={styles.noAdminCount}>{(directory.noAdminCount || 0).toLocaleString("en-GB")}</strong>
          </div>
        </div>

        {schoolDirectory}
      </>}
    </div>
  );
};

export default FranchiseSchools;
